import type { StoreTypeface } from '@avrahami/contracts';

/**
 * The store's typefaces on the flyer (adr/0050): the same families as its storefront, served with
 * the admin (all under the SIL Open Font License). A canvas draws with fonts already loaded, so
 * the files of the store's typeface are loaded before it draws, and no other store's.
 */

/** A family's file. With a `range`, the file holds only those characters. */
interface FontFile {
  url: string;
  weight: string;
  range?: string;
}

/** The characters of the Hebrew files; the Latin files hold everything else (the digits too). */
const HEBREW = 'U+0307-0308, U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F';
const LATIN =
  'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';

const split = (weight: string, hebrew: string, latin: string): FontFile[] => [
  { url: hebrew, weight, range: HEBREW },
  { url: latin, weight, range: LATIN },
];

// Each address is written out in full, so the bundler serves the file.
const FAMILIES = {
  'Flyer Assistant': (): FontFile[] => [
    { url: new URL('./fonts/Assistant-Regular.woff2', import.meta.url).href, weight: '400' },
    { url: new URL('./fonts/Assistant-SemiBold.woff2', import.meta.url).href, weight: '600' },
    { url: new URL('./fonts/Assistant-Bold.woff2', import.meta.url).href, weight: '700' },
    { url: new URL('./fonts/Assistant-ExtraBold.woff2', import.meta.url).href, weight: '800' },
  ],
  'Flyer Secular One': () =>
    split(
      '400',
      new URL('./fonts/SecularOne-400-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/SecularOne-400-latin.woff2', import.meta.url).href,
    ),
  'Flyer IBM Plex': () => [
    ...split(
      '600',
      new URL('./fonts/IBMPlexSansHebrew-600-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/IBMPlexSansHebrew-600-latin.woff2', import.meta.url).href,
    ),
    ...split(
      '700',
      new URL('./fonts/IBMPlexSansHebrew-700-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/IBMPlexSansHebrew-700-latin.woff2', import.meta.url).href,
    ),
  ],
  'Flyer Rubik': () =>
    split(
      '400 800',
      new URL('./fonts/Rubik-400-800-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/Rubik-400-800-latin.woff2', import.meta.url).href,
    ),
  'Flyer Frank Ruhl': () =>
    split(
      '500 800',
      new URL('./fonts/FrankRuhlLibre-500-800-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/FrankRuhlLibre-500-800-latin.woff2', import.meta.url).href,
    ),
  'Flyer Heebo': () =>
    split(
      '100 900',
      new URL('./fonts/Heebo-100-900-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/Heebo-100-900-latin.woff2', import.meta.url).href,
    ),
  'Flyer Noto Sans': () =>
    split(
      '100 900',
      new URL('./fonts/NotoSansHebrew-100-900-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/NotoSansHebrew-100-900-latin.woff2', import.meta.url).href,
    ),
  'Flyer Noto Serif': () =>
    split(
      '100 900',
      new URL('./fonts/NotoSerifHebrew-100-900-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/NotoSerifHebrew-100-900-latin.woff2', import.meta.url).href,
    ),
  'Flyer Suez One': () =>
    split(
      '400',
      new URL('./fonts/SuezOne-400-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/SuezOne-400-latin.woff2', import.meta.url).href,
    ),
  'Flyer Karantina': () =>
    split(
      '700',
      new URL('./fonts/Karantina-700-hebrew.woff2', import.meta.url).href,
      new URL('./fonts/Karantina-700-latin.woff2', import.meta.url).href,
    ),
} satisfies Record<string, () => FontFile[]>;

type FlyerFamily = keyof typeof FAMILIES;

/** The type of a flyer: headings and prices in `display`, everything else in `body`. */
export interface FlyerType {
  display: FlyerFamily;
  displayWeight: number;
  /** A narrow or small family is set larger to stand as tall as the others. */
  displayScale: number;
  body: FlyerFamily;
}

export const FLYER_TYPES: Readonly<Record<StoreTypeface, FlyerType>> = {
  INDUSTRIAL: {
    display: 'Flyer Secular One',
    displayWeight: 400,
    displayScale: 1,
    body: 'Flyer IBM Plex',
  },
  MODERN: { display: 'Flyer Rubik', displayWeight: 700, displayScale: 1, body: 'Flyer Rubik' },
  CLASSIC: {
    display: 'Flyer Frank Ruhl',
    displayWeight: 800,
    displayScale: 1.05,
    body: 'Flyer Assistant',
  },
  HEEBO: { display: 'Flyer Heebo', displayWeight: 800, displayScale: 1, body: 'Flyer Heebo' },
  ASSISTANT: {
    display: 'Flyer Assistant',
    displayWeight: 800,
    displayScale: 1.05,
    body: 'Flyer Assistant',
  },
  NOTO: {
    display: 'Flyer Noto Serif',
    displayWeight: 700,
    displayScale: 1,
    body: 'Flyer Noto Sans',
  },
  SUEZ: {
    display: 'Flyer Suez One',
    displayWeight: 400,
    displayScale: 1,
    body: 'Flyer Assistant',
  },
  KARANTINA: {
    display: 'Flyer Karantina',
    displayWeight: 700,
    displayScale: 1.3,
    body: 'Flyer Noto Sans',
  },
};

const loading = new Map<StoreTypeface, Promise<void>>();

/** Loads a typeface's files once per page; a failure leaves the fallback fonts. */
export function loadFlyerFonts(typeface: StoreTypeface): Promise<void> {
  const known = loading.get(typeface);
  if (known !== undefined) return known;
  const { display, body } = FLYER_TYPES[typeface];
  const load = Promise.all(
    [...new Set([display, body])].flatMap((family) =>
      FAMILIES[family]().map(async ({ url, weight, range }) => {
        const face = new FontFace(family, `url(${url})`, {
          weight,
          ...(range === undefined ? {} : { unicodeRange: range }),
        });
        document.fonts.add(await face.load());
      }),
    ),
  ).then(
    () => undefined,
    () => undefined,
  );
  loading.set(typeface, load);
  return load;
}
